// On-device SVG → WebP bitmap cache for card backgrounds.
//
// Heavy halftone SVGs (some 10–15 MB) cause jank when the browser rasterizes
// them lazily on first composite, especially during card-slam / hover-scale
// animations. Pre-rasterizing at build time was rejected because halftone
// patterns rely on the user's device pixel grid for correct AA, and the SVGs
// have hand-picked viewBox aspects that need to look right across the full
// 65:48 → 77:15 card aspect range.
//
// This module rasterizes on-device once per SVG, stores the result as a WebP
// blob in IndexedDB, and serves a blob URL for use as CSS background-image.
// First view of any card pays the rasterization cost once (off main thread
// internally); every subsequent view this session or in future sessions is a
// fast IDB-blob decode.
//
// Sizing strategy: target = "just enough to cover the widest plausible card
// render at DPR 3, scaled to the SVG's native aspect ratio". Uses *aspect
// ratio* (not absolute pixels) as the size anchor, so a 48×48 SVG and a
// 4800×4800 SVG produce the same target bitmap (both aspect = 1.0). This
// preserves designer-chosen aspect ratios while side-stepping the tiny-SVG
// case where naturalWidth might be in the dozens.

// Widest card display in device pixels at DPR 3:
// 240 CSS px tall (fixed driver-card height) × 77:15 widest aspect = 1232 CSS wide.
// × DPR 3 → 3696 × 720 device px. Display aspect 5.13.
const DISPLAY_W_DEV = 3696;
const DISPLAY_H_DEV = 720;
const DISPLAY_ASPECT = DISPLAY_W_DEV / DISPLAY_H_DEV;

// Fallback when naturalWidth / naturalHeight return 0 (rare: SVG with only
// viewBox, no width/height attrs, on browsers that don't infer). Assumes
// roughly square - produces a 3696×3696 bitmap, which covers any reasonable
// card aspect with downscale-for-free at display time.
const FALLBACK_ASPECT = 1;

function targetDims(naturalW: number, naturalH: number): { w: number; h: number } {
  const aspect = naturalW > 0 && naturalH > 0 ? naturalW / naturalH : FALLBACK_ASPECT;
  if (aspect >= DISPLAY_ASPECT) {
    const h = DISPLAY_H_DEV;
    return { w: Math.round(h * aspect), h };
  }
  const w = DISPLAY_W_DEV;
  return { w, h: Math.round(w / aspect) };
}

const memCache = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

// IDB is used in dev too. Trade-off: editing an SVG's contents in dev won't
// bust the cache (Vite serves dev asset URLs at stable paths, so the key
// doesn't change). When iterating on SVG content, wipe IDB manually via
// DevTools → Application → IndexedDB → `card-bg-cache`. In prod, Vite
// content-hashes asset URLs so edits naturally bust the cache.

// Returns a blob URL of the rasterized WebP for use as CSS background-image.
// Resolves immediately on memory-cache hit, ~10–30 ms on IDB hit, or
// ~80 ms–1.5 s on miss (rasterization runs off main thread internally).
export function getCardBgUrl(svgUrl: string): Promise<string> {
  if (!svgUrl || typeof window === "undefined") return Promise.resolve(svgUrl);
  const hit = memCache.get(svgUrl);
  if (hit) return Promise.resolve(hit);
  const pending = inflight.get(svgUrl);
  if (pending) return pending;
  const promise = loadOrRasterize(svgUrl)
    .then((url) => {
      memCache.set(svgUrl, url);
      return url;
    })
    .finally(() => {
      inflight.delete(svgUrl);
    });
  inflight.set(svgUrl, promise);
  return promise;
}

// Best-effort warm during idle time. Failures are silent; the lazy path will
// re-attempt on actual first view.
export function prewarmCardBg(svgUrl: string): void {
  if (!svgUrl || typeof window === "undefined") return;
  const schedule =
    typeof window.requestIdleCallback === "function"
      ? (cb: () => void) => window.requestIdleCallback(cb, { timeout: 2000 })
      : (cb: () => void) => window.setTimeout(cb, 200);
  schedule(() => {
    void getCardBgUrl(svgUrl).catch(() => undefined);
  });
}

async function loadOrRasterize(svgUrl: string): Promise<string> {
  const cached = await idbGet(svgUrl);
  if (cached) return URL.createObjectURL(cached);
  const blob = await rasterize(svgUrl);
  void idbPut(svgUrl, blob);
  return URL.createObjectURL(blob);
}

// Drops IDB entries whose URLs are no longer in the current catalog. Handles
// deploys where re-triage / catalog cleanup renamed or removed entries,
// bounding orphan growth. No-op on browsers without IndexedDB.
export async function gcOrphans(knownUrls: Set<string>): Promise<void> {
  const db = await getDB();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const req = store.getAllKeys();
      req.onsuccess = () => {
        for (const key of req.result) {
          if (typeof key === "string" && !knownUrls.has(key)) {
            store.delete(key);
          }
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

async function rasterize(svgUrl: string): Promise<Blob> {
  const img = new Image();
  img.decoding = "async";
  img.src = svgUrl;
  await img.decode();

  const { w, h } = targetDims(img.naturalWidth, img.naturalHeight);

  const canvas =
    typeof OffscreenCanvas !== "undefined"
      ? new OffscreenCanvas(w, h)
      : Object.assign(document.createElement("canvas"), { width: w, height: h });
  const ctx = canvas.getContext("2d") as
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D
    | null;
  if (!ctx) throw new Error("card-bg-cache: 2d context unavailable");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  // No crop, no aspect change: draw the full SVG to the full canvas. The
  // canvas matches the SVG's aspect (by construction of targetDims), so this
  // is a 1:1 vector rasterization at the target resolution.
  ctx.drawImage(img, 0, 0, w, h);

  if (canvas instanceof OffscreenCanvas) {
    return await canvas.convertToBlob({ type: "image/webp", quality: 0.92 });
  }
  return await new Promise<Blob>((resolve, reject) => {
    (canvas as HTMLCanvasElement).toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
      "image/webp",
      0.92,
    );
  });
}

// ── IndexedDB ────────────────────────────────────────────────────────────────
const DB_NAME = "card-bg-cache";
const STORE = "bitmaps";
let dbPromise: Promise<IDBDatabase | null> | null = null;

function getDB(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    if (typeof indexedDB === "undefined") {
      resolve(null);
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => {
      const db = req.result;
      // Drop the cached handle whenever the connection becomes invalid so the
      // next getDB() re-opens (and re-creates the object store if the user
      // wiped IDB via DevTools or `indexedDB.deleteDatabase`).
      db.onclose = () => { dbPromise = null; };
      db.onversionchange = () => { db.close(); dbPromise = null; };
      resolve(db);
    };
    req.onerror = () => resolve(null);
  });
  return dbPromise;
}

async function idbGet(key: string): Promise<Blob | null> {
  const db = await getDB();
  if (!db) return null;
  return new Promise<Blob | null>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => {
        const v = req.result;
        resolve(v instanceof Blob ? v : null);
      };
      req.onerror = () => resolve(null);
    } catch {
      // Stale handle (user wiped IDB, browser missed onclose). Force re-open next call.
      dbPromise = null;
      resolve(null);
    }
  });
}

async function idbPut(key: string, blob: Blob): Promise<void> {
  const db = await getDB();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(blob, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      dbPromise = null;
      resolve();
    }
  });
}
